import type { ScaleLinear } from 'd3-scale';

import { scaleLinear } from 'd3-scale';

type ChartLinearScale = ScaleLinear<number, number, never>;

export interface ChartPointerBounds {
  left: number;
  width: number;
}

const normalizeRange = (range: readonly [number, number]): [number, number] => [range[0], range[1]];

export const buildValueScale = (values: readonly number[], range: readonly [number, number]): ChartLinearScale => {
  const finiteMaximum = values.reduce(
    (maximum, value) => (Number.isFinite(value) ? Math.max(maximum, value) : maximum),
    0,
  );
  const domainMaximum = finiteMaximum > 0 ? finiteMaximum : 1;

  return scaleLinear<number>().domain([0, domainMaximum]).range(normalizeRange(range)).nice();
};

export const buildValueTicks = (scale: ChartLinearScale, count = 5): number[] => scale.ticks(Math.max(1, count));

export const buildExtentScale = (values: readonly number[], range: readonly [number, number]): ChartLinearScale => {
  const finiteValues = values.filter((value) => Number.isFinite(value));

  if (finiteValues.length === 0) {
    return scaleLinear<number>().domain([0, 1]).range(normalizeRange(range));
  }

  const minimum = Math.min(...finiteValues);
  const maximum = Math.max(...finiteValues);

  if (minimum === maximum) {
    const padding = Math.max(1, Math.abs(minimum) * 0.01);

    return scaleLinear<number>()
      .domain([minimum - padding, maximum + padding])
      .range(normalizeRange(range));
  }

  return scaleLinear<number>().domain([minimum, maximum]).range(normalizeRange(range));
};

const PADDING_RATIO = 0.12;

/**
 * Шкала по размаху значений с отступом от крайних точек: для уровня, который
 * от нуля выглядит плоским. Применима только к маркам, кодирующим значение
 * положением (линия), но не длиной (столбики) — там база обязана быть нулевой.
 */
export const buildPaddedExtentScale = (
  values: readonly number[],
  range: readonly [number, number],
): ChartLinearScale => {
  const finiteValues = values.filter((value) => Number.isFinite(value));

  if (finiteValues.length === 0) {
    return buildExtentScale([], range);
  }

  const minimum = Math.min(...finiteValues);
  const maximum = Math.max(...finiteValues);
  const padding = Math.max(1, (maximum - minimum) * PADDING_RATIO);

  return buildExtentScale([minimum - padding, maximum + padding], range);
};

export const buildIndexScale = (count: number, range: readonly [number, number]): ChartLinearScale => {
  if (count <= 1) {
    const midpoint = (range[0] + range[1]) / 2;

    return scaleLinear<number>().domain([0, 1]).range([midpoint, midpoint]);
  }

  return scaleLinear<number>()
    .domain([0, count - 1])
    .range(normalizeRange(range));
};

export const buildLabelIndexes = (count: number, maxLabels = 6): Set<number> => {
  if (count <= 0 || maxLabels <= 0) {
    return new Set();
  }

  if (maxLabels === 1) {
    return new Set([0]);
  }

  const lastIndex = count - 1;
  const step = Math.max(1, Math.ceil(count / maxLabels));
  const indexes = Array.from({ length: count }, (_, index) => index).filter(
    (index) => index % step === 0 || index === lastIndex,
  );

  return new Set(indexes);
};

export const resolvePointerIndex = (clientX: number, bounds: ChartPointerBounds, count: number): number | null => {
  if (count <= 0) {
    return null;
  }

  if (count === 1 || bounds.width <= 0) {
    return 0;
  }

  const ratio = Math.min(1, Math.max(0, (clientX - bounds.left) / bounds.width));

  return Math.round(ratio * (count - 1));
};
